import React, { useState, useEffect, useMemo, useRef } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, EnvelopeIcon, ClockIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { EmailContact, EmailTemplate, PersonalizedMessage } from '../../types/email';
import templateApi from '../../repositories/template';
import contactApi from '../../repositories/contact';
import { emailLogsApi } from '../../repositories/emailLogs';
import { fromBase64 } from '../../utils/base64';
import SegmentedTabs, { SegmentedTabItem } from '../ui/SegmentedTabs';
import { Skeleton } from '../ui/skeleton';
import CharacterCounterDisplay from '../common/CharacterCounterDisplay';

interface EmailPitchModalProps {
  isOpen: boolean;
  onClose: () => void;
  // subject is optional in payload so parent handlers that don't use it remain compatible
  onSend: (data: {
    contactIds: any[];
    templateId: any;
    subject: string;
    templateContent?: string;
    selectedContacts?: EmailContact[];
    /**
     * Per-recipient notes that replace {{personalizedMessage}} in the template. Only present
     * when the chosen template actually contains that merge field.
     */
    personalizedMessages?: PersonalizedMessage[];
    /** ISO 8601 datetime — only present when the user chose "Schedule Email" */
    scheduledFor?: string;
  }) => void;
  pitchData?: any;
  /** Grouped mode: pass multiple pitches to send as a single grouped email */
  pitches?: any[];
  selectedDSPs?: string[];
  /** Pre-populate modal with saved draft email data */
  initialEmailData?: {
    contactIds: any[];
    templateId: string;
    subject: string;
    personalizedMessages?: PersonalizedMessage[];
  };
  /**
   * Show a "Schedule Email" option alongside "Send Email". Only meaningful for call sites
   * where onSend triggers an immediate/scheduled send (e.g. Send Pitch tab) — call sites that
   * just stage email config for later submission (pitch form pages) should leave this off.
   */
  allowScheduling?: boolean;
}

const MIN_SCHEDULE_BUFFER_MS = 60_000;
// The server rejects anything scheduled more than 90 days out. Mirrored here so the
// picker can bound the input and the error is caught before a round-trip.
const MAX_SCHEDULE_AHEAD_DAYS = 90;
const MAX_SCHEDULE_AHEAD_MS = MAX_SCHEDULE_AHEAD_DAYS * 24 * 60 * 60 * 1000;

const pad = (n: number) => String(n).padStart(2, '0');
// Format a Date as the YYYY-MM-DDTHH:mm string a datetime-local input expects.
const toLocalInputValue = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

// Local datetime-local min value (now + buffer), formatted as YYYY-MM-DDTHH:mm.
// Rounded UP to the next whole minute: the input only has minute precision, so rounding
// down would offer a slot that is less than the buffer away and fails validation on click
// (e.g. at 10:00:30 a floored min of 10:01 is only 30s out).
const minScheduleLocal = () => {
  const d = new Date(Date.now() + MIN_SCHEDULE_BUFFER_MS);
  if (d.getSeconds() > 0 || d.getMilliseconds() > 0) {
    d.setSeconds(60, 0);
  }
  return toLocalInputValue(d);
};

// Upper bound for the picker so a date the server will reject can't be chosen at all.
// Rounded DOWN so the offered slot always stays inside the server's window.
const maxScheduleLocal = () => {
  const d = new Date(Date.now() + MAX_SCHEDULE_AHEAD_MS);
  d.setSeconds(0, 0);
  return toLocalInputValue(d);
};

// Single source of truth for schedule-time validity: returns the error message for a
// chosen datetime, or null when it's acceptable. Used by the live inline error, the
// button's disabled state, and handleSend, so all three can never disagree.
const scheduleTimeError = (value: string, nowMs: number): string | null => {
  if (!value) return 'Pick a date and time to schedule for';
  const ms = new Date(value).getTime();
  if (Number.isNaN(ms)) return 'Enter a valid date and time';
  if (ms < nowMs + MIN_SCHEDULE_BUFFER_MS) return 'Scheduled time must be at least a minute from now';
  if (ms > nowMs + MAX_SCHEDULE_AHEAD_MS) {
    return `Scheduled time cannot be more than ${MAX_SCHEDULE_AHEAD_DAYS} days from now`;
  }
  return null;
};

const MAX_RECIPIENTS = 30;

// Merge field a template uses to opt into per-recipient notes. This modal is the only
// editor for them — the pitch form used to carry a second one for the same state.
const PERSONALIZED_MESSAGE_FIELD = '{{personalizedMessage}}';
const MAX_PERSONAL_MESSAGE_LENGTH = 500;

// DSP configuration with display names and playlist fields
const DSP_CONFIG: Record<string, { name: string; playlistLabel: string; playlistField: string }> = {
  spotify: { name: 'Spotify', playlistLabel: 'Spotify Playlist', playlistField: 'spotifyPlaylist' },
  amazon: { name: 'Amazon Music', playlistLabel: 'Amazon Music Playlist', playlistField: 'amazonMusicPlaylist' },
  deezer: { name: 'Deezer', playlistLabel: 'Deezer Playlist', playlistField: 'deezerPlaylist' },
};

// Helper to get DSP display name
const getDSPName = (dsp: string): string => DSP_CONFIG[dsp.toLowerCase()]?.name || dsp;

// Type for storing decoded template data
interface DecodedTemplate {
  greetingText: string;
  pitchDataContent: string;
  closingText: string;
}

// Shared control styling, kept in sync with the inputs and buttons used across the
// revamped app. Inputs signal focus with a border-colour change only (no ring) —
// see the Contacts search bar for the same treatment.
const inputBaseClass =
  'p-2 text-sm text-black bg-white border rounded-md outline-none transition-colors placeholder-neutral-400 focus:border-primary-500';

const primaryButtonClass =
  'border border-transparent inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors rounded-md bg-primary-700 hover:bg-primary-800 focus:outline-none focus-visible:border-primary-950 ' +
  // Deliberate disabled treatment: a neutral fill rather than a faded primary, so a
  // button that can't be submitted yet never reads as "a lighter shade of the same button".
  'disabled:bg-neutral-200 disabled:text-neutral-500 disabled:cursor-not-allowed disabled:hover:bg-neutral-200';

const secondaryButtonClass =
  'px-4 py-2 text-sm font-medium transition-colors bg-white border rounded-md text-primary-700 border-primary-300 hover:bg-primary-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed focus-visible:border-primary-700';

const checkboxClass =
  'w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700';

// Key used to persist an in-progress draft (recipients/template/subject) across accidental
// refreshes so users don't lose their selections and risk re-picking already-emailed contacts.
const DRAFT_KEY_PREFIX = 'emailPitchDraft:';

const draftStorageKey = (pitchIds: number[]): string =>
  `${DRAFT_KEY_PREFIX}${[...pitchIds].sort((a, b) => a - b).join('-')}`;

// Drop every persisted draft, not just the currently-loaded pitch's. The modal can be
// opened against several pitches during one mount, leaving a key behind under each set
// of ids, and none of them outlive the component.
// Keys are collected before removal because removeItem re-indexes sessionStorage mid-loop.
const clearAllDrafts = () => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(DRAFT_KEY_PREFIX)) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Ignore storage errors — draft cleanup is best-effort
  }
};

interface StoredDraft {
  selectedContactIds: number[];
  selectedTemplateId: string | null;
  subject: string;
  personalizedMessages?: PersonalizedMessage[];
}

const wrapInQuotes = (html: string) =>
  html.replace(/^(<[^>]+>)/, '$1"').replace(/(<\/[^>]+>)$/, '"$1');

const Spinner: React.FC<{ className?: string }> = ({ className = 'w-8 h-8 border-4 border-neutral-100 border-t-primary-700' }) => (
  <div className={`${className} rounded-full animate-spin`} />
);

/**
 * Placeholder for the preview panes. Matches PreviewIframe's framed box and
 * h-64 body so switching tabs doesn't resize the pane, and reads as lines of an
 * email — greeting, paragraphs, sign-off — rather than a spinner on blank space.
 */
const PreviewSkeleton: React.FC = () => (
  <div className="flex-1 overflow-hidden border rounded-lg border-neutral-200" aria-busy="true">
    <div className="p-5 space-y-3 bg-white h-64">
      <Skeleton className="w-40 h-4" />
      <Skeleton className="w-full h-3" />
      <Skeleton className="w-full h-3" />
      <Skeleton className="w-5/6 h-3" />
      <div className="pt-3 space-y-3">
        <Skeleton className="w-full h-3" />
        <Skeleton className="w-2/3 h-3" />
      </div>
      <Skeleton className="w-32 h-3 mt-4" />
    </div>
  </div>
);

const PreviewIframe: React.FC<{ content: string; title: string }> = ({ content, title }) => (
  <div className="flex-1 overflow-hidden border rounded-lg border-neutral-200">
    <iframe
      srcDoc={`<!DOCTYPE html><html><head><meta charset="UTF-8"/><meta name="viewport" content="width=device-width,initial-scale=1.0"/></head><body style="margin:0;padding:20px;background-color:#ffffff;font-family:Arial,sans-serif;font-size:14px;color:#222;">${content}</body></html>`}
      title={title}
      className="w-full h-64 bg-white"
      sandbox="allow-same-origin"
    />
  </div>
);

const formatReleaseTitle = (title: string, configuration: string | string[]): string => {
  if (!configuration || (Array.isArray(configuration) && configuration.length === 0)) return `"<strong>${title}</strong>"`;
  const configs = Array.isArray(configuration)
    ? configuration
    : configuration.split(',').map(c => c.trim());
  const isSingle = configs.some(c => c?.toLowerCase() === 'single');
  return isSingle ? `"<strong>${title}</strong>"` : `<em><strong>${title}</strong></em>`;
};

const EmailPitchModal: React.FC<EmailPitchModalProps> = ({
  isOpen,
  onClose,
  onSend,
  pitchData,
  pitches,
  selectedDSPs = [],
  initialEmailData,
  allowScheduling = false,
}) => {
  const isGrouped = Array.isArray(pitches) && pitches.length > 1;
  const [contacts, setContacts] = useState<EmailContact[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  // Per-recipient notes for templates containing {{personalizedMessage}}, plus the ids of the
  // rows whose editor is currently open (purely presentational, so not part of the draft).
  const [personalizedMessages, setPersonalizedMessages] = useState<PersonalizedMessage[]>([]);
  const [expandedMessageIds, setExpandedMessageIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'firstName' | 'lastName' | 'email'>('firstName');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('contacts');
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [errors, setErrors] = useState<{ subject?: string; contacts?: string; scheduledFor?: string }>({});
  // Store original decoded template (never modify this directly)
  const [templateData, setTemplateData] = useState<DecodedTemplate | null>(null);

  // Scheduling state (only relevant when allowScheduling is true)
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const CONTACTS_PER_PAGE = 20;

  // Contacts already emailed for the pitch(es) currently open in the modal
  const [alreadyEmailedIds, setAlreadyEmailedIds] = useState<Set<number>>(new Set());

  const pitchIds = useMemo(() => {
    if (isGrouped) return pitches!.map((p) => p.id).filter((id) => id != null);
    return pitchData?.id != null ? [pitchData.id] : [];
  }, [isGrouped, pitches, pitchData]);

  // Filter DSPs that have playlist config for tabs
  const dspTabs = useMemo(() => {
    if (isGrouped) return [];
    return selectedDSPs.filter(dsp => dsp !== 'email' && DSP_CONFIG[dsp.toLowerCase()]);
  }, [selectedDSPs, isGrouped]);

  // Check if there are DSPs without playlist config (for "Others" tab)
  const hasOthersTab = useMemo(() => {
    if (isGrouped) return false;
    return selectedDSPs.some(dsp => dsp === 'email' || !DSP_CONFIG[dsp.toLowerCase()]);
  }, [selectedDSPs, isGrouped]);

  // Tracks which pitch(es) key the currently-loaded state (selectedContactIds etc.) is
  // allowed to persist under. Set to null while a pitch switch is in flight so the persist
  // effect (which reads state from the SAME render as the reset, before it commits) can't
  // flush the outgoing pitch's selections into the incoming pitch's draft key.
  const draftKeyRef = useRef<string | null>(null);
  const loadedPitchKeyRef = useRef<string | null>(null);

  // Fetch contacts and templates on mount / whenever the target pitch(es) change
  useEffect(() => {
    if (isOpen) {
      fetchData();
      setActiveTab('contacts');
      setTemplateData(null);
      setCurrentPage(1);
      setContacts([]);
      setSearchQuery('');
      setSearchType('firstName');
      setErrors({});
      setIsScheduled(false);
      setScheduledFor('');
      setAlreadyEmailedIds(new Set());
      setExpandedMessageIds(new Set());

      const nextKey = pitchIds.length > 0 ? draftStorageKey(pitchIds) : null;

      // Block persistence until the reset below has actually committed (next effect pass) —
      // otherwise the persist effect below would run this same commit with state that still
      // belongs to whichever pitch was previously loaded.
      draftKeyRef.current = null;

      // Pre-populate from saved draft email data
      if (initialEmailData) {
        setSelectedContactIds(initialEmailData.contactIds);
        setSelectedTemplateId(initialEmailData.templateId);
        setSubject(initialEmailData.subject);
        setPersonalizedMessages(initialEmailData.personalizedMessages ?? []);
      } else if (pitchIds.length > 0) {
        // Restore an in-progress draft (e.g. after an accidental refresh) so recipients,
        // template, and subject aren't silently lost and re-selected as duplicates.
        let restored: StoredDraft | null = null;
        try {
          const raw = sessionStorage.getItem(nextKey!);
          if (raw) restored = JSON.parse(raw);
        } catch {
          // Ignore malformed/inaccessible storage and fall back to a blank draft
        }
        setSelectedContactIds(restored?.selectedContactIds ?? []);
        setSelectedTemplateId(restored?.selectedTemplateId ?? null);
        setSubject(restored?.subject ?? '');
        setPersonalizedMessages(restored?.personalizedMessages ?? []);
      } else {
        setSelectedContactIds([]);
        setSelectedTemplateId(null);
        setSubject('');
        setPersonalizedMessages([]);
      }

      // Fetch contacts already emailed for this pitch (or pitches, in grouped mode) so
      // they can be flagged in the recipient list and avoid accidental duplicate sends.
      if (pitchIds.length > 0) {
        Promise.all(pitchIds.map((id) => emailLogsApi.getEmailLogsByPitch(id)))
          .then((responses) => {
            const ids = new Set<number>();
            responses.forEach((res) => {
              const logs: any[] = res?.data?.success ? (res.data.emailLogs ?? []) : [];
              logs.forEach((l: any) => {
                if (l?.contact?.id != null) ids.add(l.contact.id);
              });
            });
            setAlreadyEmailedIds(ids);
          })
          .catch(() => setAlreadyEmailedIds(new Set()));
      }

      loadedPitchKeyRef.current = nextKey;
    } else {
      draftKeyRef.current = null;
      loadedPitchKeyRef.current = null;
    }
  }, [isOpen, pitchIds, initialEmailData]);

  // Once the state values above have actually committed for the loaded pitch key, allow
  // the persist effect to start writing under that key.
  useEffect(() => {
    draftKeyRef.current = loadedPitchKeyRef.current;
  }, [selectedContactIds, selectedTemplateId, subject, personalizedMessages]);

  // Persist the in-progress draft so a refresh while the modal is open doesn't clear it.
  // Guarded by draftKeyRef so a write can't land under the new pitch's key using state
  // that still belongs to the previously-open pitch (see comments above).
  useEffect(() => {
    if (!isOpen || pitchIds.length === 0) return;
    const key = draftStorageKey(pitchIds);
    if (draftKeyRef.current !== key) return;
    const draft: StoredDraft = { selectedContactIds, selectedTemplateId, subject, personalizedMessages };
    try {
      sessionStorage.setItem(key, JSON.stringify(draft));
    } catch {
      // Storage may be unavailable (e.g. private browsing) — draft recovery is best-effort
    }
  }, [isOpen, pitchIds, selectedContactIds, selectedTemplateId, subject, personalizedMessages]);

  // Drafts are scoped to this component's lifetime: unmounting means the user navigated
  // away from the page hosting the modal, so anything in progress is abandoned.
  // This deliberately does not fire on a browser refresh or tab close — React skips
  // cleanup there — which is exactly what preserves the refresh recovery above.
  // Note: re-enabling React.StrictMode would break that recovery, since its simulated
  // mount/unmount pass would run this cleanup before the user can reopen the modal.
  useEffect(() => {
    return clearAllDrafts;
  }, []);

  // Clear the persisted draft once it's no longer needed (sent or explicitly closed)
  const clearDraft = () => {
    if (pitchIds.length === 0) return;
    try {
      sessionStorage.removeItem(draftStorageKey(pitchIds));
    } catch {
      // Ignore storage errors
    }
  };

  // Fetch contacts when search query changes (with debounce effect)
  useEffect(() => {
    if (isOpen && !loading) {
      const debounceTimer = setTimeout(() => {
        setCurrentPage(1);
        setContacts([]);
        fetchContacts(1, true);
      }, 300);
      return () => clearTimeout(debounceTimer);
    }
  }, [searchQuery, searchType]);

  // Fetch template data when templateId changes
  useEffect(() => {
    if (selectedTemplateId) {
      fetchTemplateData(selectedTemplateId);
    }
  }, [selectedTemplateId, isOpen]);

  // Build preview content from original template (no API call, instant)
  const previewContent = useMemo(() => {
    if (activeTab === 'contacts' || !templateData || !pitchData) return '';

    // Always start from ORIGINAL template data
    let pitchDataContent = templateData.pitchDataContent;

    // Add playlist fields based on DSP type
    if (activeTab === 'pitchOwner') {
      // Pitch Owner: Add ALL playlist fields that have values
      Object.entries(DSP_CONFIG).forEach(([, config]) => {
        const { playlistLabel, playlistField } = config;
        if (pitchData?.[playlistField]?.length > 0) {
          pitchDataContent = pitchDataContent.replace(
            "</div>",
            `<p style="margin:0 0 8px 0;"><strong><u>${playlistLabel}:</u></strong> {{${playlistField}}}</p></div>`
          );
        }
      });
    } else if (activeTab !== 'others') {
      // DSP-specific playlist fields (single DSP tab)
      const dspConfig = DSP_CONFIG[activeTab.toLowerCase()];
      if (dspConfig) {
        const { playlistLabel, playlistField } = dspConfig;
        if (pitchData?.[playlistField]?.length > 0) {
          pitchDataContent = pitchDataContent.replace(
            "</div>",
            `<p style="margin:0 0 8px 0;"><strong><u>${playlistLabel}:</u></strong> {{${playlistField}}}</p></div>`
          );
        }
      }
    }
    // others: No playlist fields added (generic email)

    // Build header: Artist Name - Release Name - Release Date
    const rawTitle = pitchData?.releaseTitle || '';
    const formattedTitle = rawTitle ? formatReleaseTitle(rawTitle, pitchData?.configuration || 'Single') : '';
    const artistName = pitchData?.artistName || '';
    const releaseDate = pitchData?.releaseDate || '';
    const headerParts = [artistName, formattedTitle, releaseDate].filter(Boolean);
    const header = headerParts.length > 0
      ? `<p style="margin:0 0 16px 0;"><strong>${headerParts.join(' - ')}</strong></p>`
      : '';

    const wrappedGreeting = wrapInQuotes(templateData.greetingText);
    const wrappedClosing = wrapInQuotes(templateData.closingText);

    // Combine: greeting → header → pitch data → closing
    let content = wrappedGreeting + '\n\n' + header + pitchDataContent + '\n\n' + wrappedClosing;

    // Genre field uses the Spotify subgenre when available, falling back to main genre
    // only when the artist wasn't pitched to Spotify (no subgenre selected).
    const spotifySubgenre = pitchData?.spotifySubgenre;
    const effectiveGenre = spotifySubgenre?.length > 0
      ? (Array.isArray(spotifySubgenre) ? spotifySubgenre.join(', ') : spotifySubgenre)
      : (Array.isArray(pitchData?.mainGenre) ? pitchData.mainGenre.join(', ') : pitchData?.mainGenre || '');

    // For DSP-specific tabs, append marketing note to shortDescription
    const dspMarketingNotes = pitchData?.dspMarketingNotes || [];
    let shortDescWithNote = pitchData?.shortDescription || '';

    if (activeTab !== 'contacts' && activeTab !== 'others' && activeTab !== 'pitchOwner') {
      const dspDisplayName = getDSPName(activeTab);
      const marketingNote = dspMarketingNotes.find(
        (n: any) => n.dspName === dspDisplayName
      );
      if (marketingNote?.note) {
        shortDescWithNote = `${shortDescWithNote} ${marketingNote.note}`;
      }
    }

    // Normalise API key casing mismatches vs template merge fields
    const normalized: Record<string, string> = {};
    Object.entries(pitchData).forEach(([k, v]) => { normalized[k] = (v as string) ?? ''; });
    normalized['releaseUpc'] = pitchData.releaseUPC ?? '';
    normalized['trackIsrc'] = pitchData.trackISRC ?? '';
    normalized['firstName'] = 'Contact';
    // Stand-in like firstName above: the real note is per recipient, so the shared preview
    // shows a placeholder rather than one contact's text or a raw merge field.
    normalized['personalizedMessage'] = '[Personalized message]';

    // Replace merge fields with pitch data for preview
    Object.entries(normalized).forEach(([key, value]) => {
      let replacementValue: string;
      if (key === 'shortDescription') {
        replacementValue = shortDescWithNote;
      } else if (key === 'releaseTitle') {
        replacementValue = formattedTitle;
      } else if (key === 'mainGenre') {
        replacementValue = effectiveGenre;
      } else {
        replacementValue = value;
      }
      content = content.replace(new RegExp(`{{${key}}}`, 'g'), replacementValue);
    });

    return content;
  }, [activeTab, templateData, pitchData]);

  // Grouped preview: greeting → per-pitch headers → closing.
  // Backend renders final body fields; frontend only shows section headers.
  const groupedPreviewContent = useMemo(() => {
    if (!isGrouped || !templateData || !pitches) return '';

    const wrappedGreeting = wrapInQuotes(templateData.greetingText);
    const wrappedClosing = wrapInQuotes(templateData.closingText);

    const sections = pitches.map((p) => {
      const formattedTitle = p?.releaseTitle
        ? formatReleaseTitle(p.releaseTitle, p?.configuration || 'Single')
        : '';
      const parts = [p?.artistName || '', formattedTitle, p?.releaseDate || ''].filter(Boolean);
      const header = parts.length
        ? `<p style="margin:0 0 8px 0;"><strong>${parts.join(' – ')}</strong></p>`
        : '';

      // Normalise API key casing mismatches vs template merge fields
      const normalized: Record<string, string> = {};
      Object.entries(p).forEach(([k, v]) => { normalized[k] = (v as string) ?? ''; });
      normalized['releaseUpc'] = p.releaseUPC ?? '';
      normalized['trackIsrc'] = p.trackISRC ?? '';

      // Genre field uses the Spotify subgenre when available, falling back to main genre
      // only when the artist wasn't pitched to Spotify (no subgenre selected).
      const pSpotifySubgenre = p?.spotifySubgenre;
      normalized['mainGenre'] = pSpotifySubgenre?.length > 0
        ? (Array.isArray(pSpotifySubgenre) ? pSpotifySubgenre.join(', ') : pSpotifySubgenre)
        : (Array.isArray(p?.mainGenre) ? p.mainGenre.join(', ') : p?.mainGenre || '');

      let pitchBlock = templateData.pitchDataContent;
      Object.entries(normalized).forEach(([key, value]) => {
        const replacement = key === 'releaseTitle' ? formattedTitle : value;
        pitchBlock = pitchBlock.replace(new RegExp(`{{${key}}}`, 'g'), replacement);
      });
      pitchBlock = pitchBlock.replace(/\{\{[^}]+\}\}/g, '');

      return `<div style="margin:0 0 24px 0; padding-bottom:16px; border-bottom:1px solid #e5e7eb;">${header}${pitchBlock}</div>`;
    }).join('');

    // Use first pitch's data to fill greeting/closing merge fields for preview
    const first = pitches[0];
    const firstTitle = first?.releaseTitle ? formatReleaseTitle(first.releaseTitle, first?.configuration || 'Single') : '';
    const firstNormalized: Record<string, string> = {};
    Object.entries(first).forEach(([k, v]) => { firstNormalized[k] = (v as string) ?? ''; });
    firstNormalized['releaseUpc'] = first.releaseUPC ?? '';
    firstNormalized['trackIsrc'] = first.trackISRC ?? '';
    firstNormalized['personalizedMessage'] = '[Personalized message]';

    let filledGreeting = wrappedGreeting;
    let filledClosing = wrappedClosing;
    Object.entries(firstNormalized).forEach(([key, value]) => {
      const replacement = key === 'releaseTitle' ? firstTitle : value;
      filledGreeting = filledGreeting.replace(new RegExp(`{{${key}}}`, 'g'), replacement);
      filledClosing = filledClosing.replace(new RegExp(`{{${key}}}`, 'g'), replacement);
    });

    return filledGreeting + '\n\n' + sections + '\n\n' + filledClosing;
  }, [isGrouped, templateData, pitches]);

  const fetchContacts = async (page: number, reset: boolean = false) => {
    if (reset) {
      setLoadingMore(true);
    }
    try {
      const params: any = {
        page,
        limit: CONTACTS_PER_PAGE,
        sortBy: 'createdAt',
        sortOrder: 'DESC',
      };

      // Add search parameters if search query exists
      if (searchQuery.trim()) {
        params.searchType = searchType;
        params.search = searchQuery.trim();
      }

      const contactsRes = await contactApi.getContacts(params);

      if (contactsRes?.data?.success) {
        const newContacts = contactsRes.data.contacts || [];
        const pagination = contactsRes.data.pagination;

        if (reset || page === 1) {
          setContacts(newContacts);
        } else {
          setContacts((prev) => [...prev, ...newContacts]);
        }

        if (pagination) {
          setTotalPages(pagination.totalPages);
          setTotalContacts(pagination.total);
          setCurrentPage(pagination.page);
        }
      }
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contactsRes, templatesRes] = await Promise.all([
        contactApi.getContacts({ page: 1, limit: CONTACTS_PER_PAGE }),
        templateApi.getTemplates(),
      ]);

      if (contactsRes?.data?.success) {
        setContacts(contactsRes.data.contacts || []);
        const pagination = contactsRes.data.pagination;
        if (pagination) {
          setTotalPages(pagination.totalPages);
          setTotalContacts(pagination.total);
          setCurrentPage(pagination.page);
        }
      }

      if (templatesRes?.data?.success) {
        const templateList = templatesRes.data.templates || [];
        setTemplates(templateList);

        // Pre-select default template only if no initial email data and no template already selected
        if (!initialEmailData && selectedTemplateId === null) {
          const defaultTemplate = templateList.find((t: EmailTemplate) => t.isDefault);
          if (defaultTemplate) {
            setSelectedTemplateId(defaultTemplate.id);
          } else if (templateList.length > 0) {
            setSelectedTemplateId(templateList[0].id);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load more contacts
  const loadMoreContacts = () => {
    if (currentPage < totalPages && !loadingMore) {
      fetchContacts(currentPage + 1, false);
    }
  };

  // Fetch template data once when template changes
  const fetchTemplateData = async (templateId: string) => {
    setLoadingTemplate(true);
    try {
      const response = await templateApi.getTemplate(templateId);

      if (response?.data?.success) {
        const template = response.data.template;

        // Decode and store original template data (never modify this)
        setTemplateData({
          greetingText: fromBase64(template.greetingText || ''),
          pitchDataContent: fromBase64(template.pitchData || ''),
          closingText: fromBase64(template.closingText || ''),
        });
      }
    } catch (error) {
      console.error('Failed to fetch template:', error);
      setTemplateData(null);
    } finally {
      setLoadingTemplate(false);
    }
  };

  // Full rendered template, used both to detect merge fields and to hand the parent the
  // content it stores alongside the pitch.
  const fullTemplateContent = useMemo(
    () =>
      templateData
        ? `${templateData.greetingText} ${templateData.pitchDataContent} ${templateData.closingText}`
        : '',
    [templateData]
  );

  // Only templates that actually reference {{personalizedMessage}} get the per-recipient
  // note editor.
  const supportsPersonalizedMessage = fullTemplateContent.includes(PERSONALIZED_MESSAGE_FIELD);

  const selectedContacts = useMemo(
    () => contacts.filter((c) => selectedContactIds.includes(c.id)),
    [contacts, selectedContactIds]
  );

  const getPersonalizedMessage = (contactId: number): string =>
    personalizedMessages.find((m) => m.contactId === contactId)?.message || '';

  const setPersonalizedMessage = (contactId: number, message: string) => {
    if (message.length > MAX_PERSONAL_MESSAGE_LENGTH) return;
    setPersonalizedMessages((prev) =>
      prev.some((m) => m.contactId === contactId)
        ? prev.map((m) => (m.contactId === contactId ? { ...m, message } : m))
        : [...prev, { contactId, message }]
    );
  };

  const toggleMessageEditor = (contactId: number) => {
    setExpandedMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  };

  // Toggle contact selection
  const toggleContact = (contactId: number) => {
    setSelectedContactIds((prev) => {
      if (prev.includes(contactId)) {
        // Collapse the note editor but keep whatever was typed, so re-selecting restores it
        setExpandedMessageIds((ids) => {
          if (!ids.has(contactId)) return ids;
          const next = new Set(ids);
          next.delete(contactId);
          return next;
        });
        return prev.filter((id) => id !== contactId);
      }
      if (prev.length >= MAX_RECIPIENTS) {
        return prev;
      }
      setErrors((e) => ({ ...e, contacts: undefined }));
      return [...prev, contactId];
    });
  };

  // Select/Deselect all visible contacts
  const toggleSelectAll = () => {
    if (selectedContactIds.length === contacts.length && contacts.length > 0) {
      setSelectedContactIds([]);
      setExpandedMessageIds(new Set());
    } else {
      const idsToSelect = contacts
        .slice(0, MAX_RECIPIENTS)
        .map((c: EmailContact) => c.id);
      setSelectedContactIds(idsToSelect);
    }
  };

  // Handle send. When `schedule` is true (only reachable when allowScheduling is on),
  // validates and attaches scheduledFor as an ISO datetime instead of sending immediately.
  const handleSend = async (schedule: boolean = false) => {
    if (!selectedTemplateId) return;

    const newErrors: { subject?: string; contacts?: string; scheduledFor?: string } = {};
    if (!subject.trim()) newErrors.subject = 'Subject is required';
    if (selectedContactIds.length === 0) newErrors.contacts = 'Select at least one recipient';
    // Same validator the inline error and the button's disabled state use, so a click can
    // never be accepted on a time the field is flagging (or rejected on one it isn't).
    // Includes the server's 90-day cap, caught here rather than as a failed request.
    if (schedule) {
      const timeError = scheduleTimeError(scheduledFor, Date.now());
      if (timeError) newErrors.scheduledFor = timeError;
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    onSend({
      contactIds: selectedContactIds,
      templateId: selectedTemplateId,
      subject,
      templateContent: fullTemplateContent,
      selectedContacts,
      // Only forwarded for templates that reference the merge field, and scoped to the
      // recipients that are still selected — notes typed for a since-removed contact are dropped.
      ...(supportsPersonalizedMessage
        ? {
          personalizedMessages: personalizedMessages.filter((m) =>
            selectedContactIds.includes(m.contactId)
          ),
        }
        : {}),
      ...(schedule && scheduledFor ? { scheduledFor: new Date(scheduledFor).toISOString() } : {}),
    });
    clearDraft();
    // onClose();
  };

  const isOverLimit = selectedContactIds.length > MAX_RECIPIENTS;
  const isAtLimit = selectedContactIds.length === MAX_RECIPIENTS;

  // A chosen time silently goes stale as the clock advances (pick 90s out, wait two
  // minutes, and it's now in the past). Nothing else re-renders on a timer, so without
  // this the button would stay enabled for a slot handleSend now rejects. Ticking every
  // second keeps the button state and the validator in agreement.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isOpen || !isScheduled) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isOpen, isScheduled]);

  // The send/schedule buttons previously keyed off the template alone, so they were
  // enabled before a recipient or subject existed — clicking then produced inline
  // errors instead of being prevented. Mirror handleSend's own preconditions here so
  // the button state matches what the click will actually accept, and so the state is
  // identical on every open regardless of what a prior attempt left behind.
  const missingRequiredFields =
    !selectedTemplateId || !subject.trim() || selectedContactIds.length === 0;
  const cannotSendNow = missingRequiredFields || isOverLimit;
  // Scheduling additionally needs a valid future datetime within the server's 90-day window.
  const scheduleError = scheduleTimeError(scheduledFor, now);
  const cannotSchedule = cannotSendNow || scheduleError !== null;

  // Shown as soon as a time is picked rather than only after a failed click. Suppressed
  // while the field is still empty so simply ticking "Schedule for later" doesn't
  // immediately read as an error. Recomputed on each tick of `now`, so a slot that goes
  // stale while the modal sits open starts reporting itself without any interaction.
  const liveScheduleError = isScheduled && scheduledFor ? scheduleError : null;
  // Prefer the live message; fall back to the submit-time error (e.g. the empty-field case).
  const displayedScheduleError = liveScheduleError ?? errors.scheduledFor;

  // Same tab set as before, expressed as data so the shared SegmentedTabs primitive can
  // render it instead of this file hand-rolling its own tab styling.
  const tabItems: SegmentedTabItem[] = [
    {
      key: 'contacts',
      label: (
        <span className="flex items-center">
          Select Recipients
          {selectedContactIds.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
              {selectedContactIds.length}
            </span>
          )}
        </span>
      ),
    },
    ...dspTabs.map((dsp) => ({ key: dsp, label: getDSPName(dsp) })),
    // Others tab - for DSPs without playlist config
    ...(hasOthersTab ? [{ key: 'others', label: 'Others' }] : []),
    // Pitch Owner tab - includes all playlist fields (hidden in grouped mode)
    ...(!isGrouped ? [{ key: 'pitchOwner', label: 'Pitch Owner' }] : []),
    // Grouped preview tab
    ...(isGrouped ? [{ key: 'grouped', label: `Preview (${pitches!.length} pitches)` }] : []),
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-lg shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-100">
          <div className="flex items-center gap-2">
            <EnvelopeIcon className="w-6 h-6 text-primary-700" />
            <h2 className="text-lg font-semibold text-black">Email Pitch</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="border border-transparent p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {loading ? (
          /*
            Shaped like the loaded modal — settings band, tab strip, then the
            h-80 recipient list — so the dialog opens at its final size instead
            of a short spinner box that jumps taller once templates and contacts
            arrive.
          */
          <div aria-busy="true">
            <div className="px-4 py-3 space-y-3 border-b border-neutral-100 bg-neutral-50">
              {/* Template, then Subject: label rail plus control, as below. */}
              <div className="flex items-center gap-3">
                <Skeleton className="w-20 h-4 shrink-0" />
                <Skeleton className="flex-1 h-9 rounded-md" />
              </div>
              <div className="flex items-center gap-3">
                <Skeleton className="w-20 h-4 shrink-0" />
                <Skeleton className="flex-1 h-9 rounded-md" />
              </div>
            </div>

            <div className="flex gap-6 px-4 py-3 border-b border-neutral-100">
              <Skeleton className="w-24 h-4" />
              <Skeleton className="w-20 h-4" />
            </div>

            <div className="p-4 space-y-4 h-80">
              <Skeleton className="w-32 h-4" />
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="w-4 h-4 rounded shrink-0" />
                  <div className="flex-1 min-w-0 space-y-2">
                    <Skeleton className="w-40 h-3.5" />
                    <Skeleton className="w-56 h-3" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Template Selection */}
            <div className="px-4 py-3 space-y-2 border-b border-neutral-100 bg-neutral-50">
              <div className="flex items-center gap-3">
                <label className="w-20 text-sm font-medium text-neutral-700 whitespace-nowrap">
                  Template
                </label>
                <select
                  value={selectedTemplateId || ''}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  className={`flex-1 cursor-pointer border-neutral-300 hover:border-primary-300 ${inputBaseClass}`}
                >
                  <option value="" disabled>
                    Choose a template...
                  </option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} {template.isDefault && '(Default)'} {template.isSystemTemplate && '⭐'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Warning: grouped mode with pitch-specific merge fields in greeting/closing */}
              {isGrouped && templateData && /\{\{(artistName|releaseTitle|releaseDate|releaseUpc|trackIsrc|mainGenre|songMood|shortDescription|longDescription)\}\}/.test(
                templateData.greetingText + templateData.closingText
              ) && (
                <div className="flex items-start gap-2 px-3 py-2 text-xs border rounded-md bg-warning-50 border-warning-200 text-warning-800">
                  <span>⚠️</span>
                  <span>Your greeting or closing contains pitch-specific fields (e.g. <strong>{'{{artistName}}'}</strong>). These will be filled using the first pitch's data. Consider using a template with a generic greeting for grouped sends.</span>
                </div>
              )}

              {/* Subject */}
              <div className="flex items-start gap-3">
                <label className="w-20 mt-2 text-sm font-medium text-neutral-700 whitespace-nowrap">Subject</label>
                <div className="flex-1">
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => { setSubject(e.target.value); setErrors((prev) => ({ ...prev, subject: undefined })); }}
                    placeholder="Email subject"
                    className={`w-full ${inputBaseClass} ${errors.subject ? 'border-danger-400' : 'border-neutral-300 hover:border-primary-300'}`}
                  />
                  {errors.subject && <p className="mt-1 text-xs text-danger-600">{errors.subject}</p>}
                </div>
              </div>

              {/* Schedule */}
              {allowScheduling && (
                <div className="flex items-start gap-3 justify-center">
                  <label className={`text-sm font-medium text-neutral-700 whitespace-nowrap w-20 ${ displayedScheduleError ? 'mt-5' : isScheduled ? 'mt-2' : ''}`}>Schedule</label>
                  <div className="flex items-center justify-between flex-1">
                    <label className="flex items-center gap-2 text-sm cursor-pointer text-neutral-700">
                      <input
                        type="checkbox"
                        checked={isScheduled}
                        onChange={(e) => {
                          setIsScheduled(e.target.checked);
                          setErrors((prev) => ({ ...prev, scheduledFor: undefined }));
                          if (!e.target.checked) setScheduledFor('');
                        }}
                        className={checkboxClass}
                      />
                      Schedule for later
                    </label>
                    {isScheduled && (
                      <div className="">
                        <input
                          type="datetime-local"
                          value={scheduledFor}
                          min={minScheduleLocal()}
                          max={maxScheduleLocal()}
                          placeholder="Select date and time"
                          onChange={(e) => { setScheduledFor(e.target.value); setErrors((prev) => ({ ...prev, scheduledFor: undefined })); }}
                          className={`w-full ${inputBaseClass} ${displayedScheduleError ? 'border-danger-400' : 'border-primary-300'}`}
                        />
                        {displayedScheduleError && <p className="mt-1 text-xs text-danger-600">{displayedScheduleError}</p>}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Tabs — the shared underline pattern, same as every other tab strip in the app */}
            <SegmentedTabs
              items={tabItems}
              value={activeTab}
              onChange={setActiveTab}
              ariaLabel="Email pitch sections"
              className="px-2 overflow-x-auto scrollbar-hide"
            />

            {/* Tab Content */}
            <div className="h-80 overflow-y-auto scrollbar-hide">
              {activeTab === 'contacts' ? (
                <>
                  {/* Contact Selection Header */}
                  <div className="p-4 border-b border-neutral-100">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-sm font-medium text-black">Select Recipients</h3>
                        <p className={`text-xs mt-1 ${isOverLimit ? 'text-danger-600 font-semibold' : 'text-neutral-500'}`}>
                          {selectedContactIds.length} of {MAX_RECIPIENTS} recipients selected
                          {isOverLimit && ' - Maximum exceeded!'} {isAtLimit && ' - Maximum reached'}
                        </p>
                        {supportsPersonalizedMessage && (
                          <p className="mt-1 text-xs text-neutral-500">
                            This template uses <span className="font-medium">{PERSONALIZED_MESSAGE_FIELD}</span> — write a note under any selected recipient.
                          </p>
                        )}
                        {errors.contacts && <p className="mt-1 text-xs text-danger-600">{errors.contacts}</p>}
                      </div>
                      <button
                        onClick={toggleSelectAll}
                        className="border border-transparent px-1 -mx-1 text-sm font-medium rounded text-primary-700 hover:text-primary-800 hover:underline focus:outline-none focus-visible:border-primary-700"
                      >
                        {selectedContactIds.length === contacts.length && contacts.length > 0 ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>

                    {/*
                      Search: the single-border, inset-divider, custom-chevron treatment
                      established on the Contacts tab, rather than two separately-bordered
                      controls sitting next to each other.
                    */}
                    <div className="flex items-stretch w-full bg-white border rounded-lg border-neutral-300 focus-within:border-primary-500">
                      <div className="relative shrink-0">
                        <select
                          value={searchType}
                          onChange={(e) => setSearchType(e.target.value as 'firstName' | 'lastName' | 'email')}
                          aria-label="Search recipients by field"
                          className="w-[104px] sm:w-auto h-full py-2 pl-3 text-sm truncate bg-transparent border-0 appearance-none cursor-pointer pr-7 text-neutral-600 focus:outline-none focus:ring-0"
                        >
                          <option value="firstName">First Name</option>
                          <option value="lastName">Last Name</option>
                          <option value="email">Email</option>
                        </select>
                        <svg className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-2 top-1/2 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      <span aria-hidden="true" className="self-stretch w-px my-1.5 bg-neutral-200" />

                      <div className="relative flex-1 min-w-0">
                        <MagnifyingGlassIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-400" />
                        <input
                          type="text"
                          placeholder={`Search by ${searchType === 'firstName' ? 'first name' : searchType === 'lastName' ? 'last name' : 'email'}...`}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full py-2 pr-4 text-sm text-black bg-transparent border-0 rounded-r-lg pl-9 placeholder-neutral-400 focus:outline-none focus:ring-0"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact List */}
                  <div className="p-4">
                    {contacts.length === 0 && !loadingMore ? (
                      <div className="flex flex-col items-center justify-center py-8 text-neutral-500">
                        <EnvelopeIcon className="w-12 h-12 mb-2 text-neutral-400" />
                        <p className="text-sm font-medium text-black">No contacts found</p>
                        {searchQuery && (
                          <p className="mt-1 text-xs text-neutral-500">Try a different search term</p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {contacts.map((contact) => {
                          const isSelected = selectedContactIds.includes(contact.id);
                          const isDisabled = !isSelected && selectedContactIds.length >= MAX_RECIPIENTS;
                          const alreadyEmailed = alreadyEmailedIds.has(contact.id);
                          // The note editor lives inside the row rather than in its own step, so
                          // recipients and their messages stay in one list.
                          const showMessageEditor = supportsPersonalizedMessage && isSelected;
                          const personalMessage = getPersonalizedMessage(contact.id);
                          const isMessageOpen = expandedMessageIds.has(contact.id);

                          return (
                            <div
                              key={contact.id}
                              className={`
                                rounded-lg border transition-all
                                ${isSelected
                                  ? 'border-primary-700 bg-primary-50'
                                  : isDisabled
                                    ? 'border-neutral-200 bg-neutral-50 opacity-50'
                                    : 'border-neutral-200 hover:border-primary-300 hover:bg-primary-50'
                                }
                              `}
                            >
                              <div
                                onClick={() => !isDisabled && toggleContact(contact.id)}
                                className={`flex items-center gap-3 p-3 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                {/* Checkbox */}
                                <div
                                  className={`
                                    flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center
                                    ${isSelected
                                      ? 'border-primary-700 bg-primary-700'
                                      : 'border-neutral-300'
                                    }
                                  `}
                                >
                                  {isSelected && (
                                    <CheckCircleIcon className="w-4 h-4 text-white" />
                                  )}
                                </div>

                                {/* Contact Info */}
                                <div className="flex-1 min-w-0">
                                  <p className="flex items-center gap-2 text-sm font-medium text-black truncate">
                                    <span className="truncate">{contact.firstName} {contact.lastName}</span>
                                    {alreadyEmailed && (
                                      <span className="flex-shrink-0 px-2 py-0.5 text-[10px] font-medium border rounded-full bg-warning-50 text-warning-800 border-warning-200">
                                        Already emailed
                                      </span>
                                    )}
                                    {showMessageEditor && personalMessage && !isMessageOpen && (
                                      <span className="flex-shrink-0 text-[10px] font-medium text-success-600">
                                        ✓ Message added
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-xs truncate text-neutral-600">
                                    {contact.company} {contact.role && `• ${contact.role}`}
                                  </p>
                                </div>

                                {/* Email */}
                                <p className="flex-shrink-0 hidden text-xs text-neutral-500 sm:block">
                                  {contact.email}
                                </p>
                              </div>

                              {/* Personalized message — replaces {{personalizedMessage}} in this
                                  recipient's copy. Sits outside the row's click target so typing
                                  here can't deselect the contact. */}
                              {showMessageEditor && (
                                <div className="px-3 pb-3">
                                  <button
                                    type="button"
                                    onClick={() => toggleMessageEditor(contact.id)}
                                    className="border border-transparent inline-flex items-center gap-1 px-1 -mx-1 text-xs font-medium rounded text-primary-700 hover:text-primary-800 focus:outline-none focus-visible:border-primary-700"
                                  >
                                    {isMessageOpen ? (
                                      <ChevronDownIcon className="w-3.5 h-3.5" />
                                    ) : (
                                      <ChevronRightIcon className="w-3.5 h-3.5" />
                                    )}
                                    {personalMessage ? 'Edit personal message' : 'Add a personal message'}
                                    <span className="font-normal text-neutral-500">(optional)</span>
                                  </button>

                                  {isMessageOpen && (
                                    <div className="relative mt-2">
                                      <textarea
                                        value={personalMessage}
                                        onChange={(e) => setPersonalizedMessage(contact.id, e.target.value)}
                                        placeholder={`Message for ${contact.firstName}...`}
                                        maxLength={MAX_PERSONAL_MESSAGE_LENGTH}
                                        rows={3}
                                        className="w-full px-3 py-2 pb-6 text-sm bg-white border rounded-md outline-none resize-none transition-colors text-neutral-700 border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
                                      />
                                      <div className="absolute bottom-2 right-2">
                                        <CharacterCounterDisplay
                                          value={personalMessage}
                                          maxLength={MAX_PERSONAL_MESSAGE_LENGTH}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Load More Button */}
                        {currentPage < totalPages && (
                          <div className="pt-3 flex justify-center">
                            <button
                              onClick={loadMoreContacts}
                              disabled={loadingMore}
                              className={secondaryButtonClass}
                            >
                              {loadingMore ? (
                                <span className="flex items-center gap-2">
                                  <Spinner className="w-4 h-4 border-2 border-primary-700 border-t-transparent" />
                                  Loading...
                                </span>
                              ) : (
                                `Load More (${contacts.length} of ${totalContacts})`
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : activeTab === 'grouped' ? (
                /* Grouped Preview Tab */
                <div className="p-4 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-black">
                      Grouped Email Preview ({pitches?.length ?? 0} pitches)
                    </h3>
                  </div>

                  {loadingTemplate ? (
                    <PreviewSkeleton />
                  ) : groupedPreviewContent ? (
                    <PreviewIframe content={groupedPreviewContent} title="Grouped Email Preview" />
                  ) : (
                    <div className="flex items-center justify-center flex-1 py-8 text-neutral-500">
                      <p className="text-sm">Select a template to see the preview</p>
                    </div>
                  )}
                </div>
              ) : (
                /* DSP Preview Tab */
                <div className="p-4 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-black">
                      {activeTab === 'pitchOwner'
                        ? 'Pitch Owner Email Preview (All Playlists)'
                        : activeTab === 'others'
                          ? 'Others Email Preview'
                          : `Email Preview for ${getDSPName(activeTab)} Contacts`}
                    </h3>
                  </div>

                  {loadingTemplate ? (
                    <PreviewSkeleton />
                  ) : previewContent ? (
                    <PreviewIframe content={previewContent} title="Email Preview" />
                  ) : (
                    <div className="flex items-center justify-center flex-1 py-8 text-neutral-500">
                      <p className="text-sm">Select a template to see the preview</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-neutral-100 bg-neutral-50">
              <button
                onClick={onClose}
                className={secondaryButtonClass}
              >
                Cancel
              </button>

              <div className="flex gap-2">
                {allowScheduling && isScheduled ? (
                  <button
                    onClick={() => handleSend(true)}
                    disabled={cannotSchedule}
                    className={primaryButtonClass}
                  >
                    <ClockIcon className="w-4 h-4" />
                    Schedule Email
                  </button>
                ) : (
                  <button
                    onClick={() => handleSend(false)}
                    disabled={cannotSendNow}
                    className={primaryButtonClass}
                  >
                    <EnvelopeIcon className="w-4 h-4" />
                    Send Email
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailPitchModal;
